// Helpers used by every Phase upload card — CSV header parsing,
// runtime estimation, parameter tooltip metadata.
//
// Niranjan, Feb 2026.

import React, { useEffect, useState } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Info, HelpCircle } from 'lucide-react';


// ─────────────────── CSV header parsing ───────────────────────────

export function parseCsvHeader(file) {
  if (!file) return Promise.resolve([]);
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result || '';
      const firstLine = text.split(/\r?\n/, 1)[0] || '';
      const cols = firstLine.split(',').map(c => c.replace(/^"|"$/g, '').trim()).filter(Boolean);
      resolve(cols);
    };
    reader.onerror = () => resolve([]);
    // Read just the first ~8 KB — enough for any header row
    reader.readAsText(file.slice(0, 8 * 1024));
  });
}


export function useCsvHeader(file) {
  const [headers, setHeaders] = useState([]);
  useEffect(() => {
    let cancelled = false;
    parseCsvHeader(file).then(cols => { if (!cancelled) setHeaders(cols); });
    return () => { cancelled = true; };
  }, [file]);
  return headers;
}


export function autoSelectColumn(headers, patterns) {
  for (const p of patterns) {
    const re = new RegExp(p, 'i');
    const hit = headers.find(h => re.test(h));
    if (hit) return hit;
  }
  return null;
}


// ─────────────────── Runtime estimator ────────────────────────────

// Calibration anchored to the audit timings on the test pod
// (P1=2s, P2=12s for 200×500 + 100 perms + 20 boot, P3=3s, P4=5s).
// We linearly extrapolate from there.

export function estimateRuntime({ phase, nIndiv = 100, nMarkers = 500, nPerm = 0, nBootstrap = 0, nReps = 10 }) {
  // Calibration anchors — seconds per (n × m) at default settings
  const baselines = {
    phase1: 2.0 * (100 / 100) * (500 / 500),
    qtl:    1.5 + (nIndiv / 200) * (nMarkers / 500) * (nPerm / 100) * 12 + (nBootstrap / 20) * 1.0,
    gwas:   3.0 * (nIndiv / 100) * (nMarkers / 500),
    gs:     5.0 * (nIndiv / 100) * (nMarkers / 500) * (nReps / 10),
  };
  const seconds = baselines[phase] || 5.0;
  if (seconds < 5) return '~3-5 sec';
  if (seconds < 60) return `~${Math.ceil(seconds)} sec`;
  if (seconds < 600) return `~${Math.ceil(seconds / 10) * 10} sec`;
  return `~${Math.ceil(seconds / 60)} min`;
}


// ────────────────── Parameter tooltip wrapper ─────────────────────
// We use Radix Tooltip directly so the help-text floats correctly
// without forcing a hidden-overflow ancestor.

export function ParamLabel({ label, hint, htmlFor, testid }) {
  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <TooltipPrimitive.Root>
        <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
          <label htmlFor={htmlFor}>{label}</label>
          {hint && (
            <TooltipPrimitive.Trigger asChild>
              <button type="button"
                className="inline-flex items-center justify-center rounded-full text-slate-300 hover:text-slate-500 transition-colors"
                aria-label={`Help — ${label}`}
                data-testid={testid ? `${testid}-tooltip-trigger` : undefined}>
                <HelpCircle size={11} />
              </button>
            </TooltipPrimitive.Trigger>
          )}
        </span>
        {hint && (
          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              side="top"
              sideOffset={6}
              className="z-50 max-w-xs rounded-md bg-slate-900 px-3 py-2 text-xs leading-relaxed text-white shadow-lg"
              data-testid={testid ? `${testid}-tooltip` : undefined}
            >
              {hint}
              <TooltipPrimitive.Arrow className="fill-slate-900" />
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        )}
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}


// ────────────────── Step-section header ───────────────────────────

export function StepHeader({ step, title, hint }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="inline-flex items-center justify-center rounded-full text-[10px] font-bold tracking-wide"
        style={{
          width: 22, height: 22,
          background: '#0F172A', color: 'white',
          letterSpacing: '0.05em',
        }}>
        {step}
      </span>
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">
        {title}
      </span>
      {hint && (
        <span className="text-[10px] text-slate-400 italic ml-1">{hint}</span>
      )}
    </div>
  );
}


// ─────────────── Runtime estimator widget ────────────────────────

export function RuntimeEstimate({ estimate, testid }) {
  if (!estimate) return null;
  return (
    <span className="text-[11px] italic text-slate-500 inline-flex items-center gap-1"
      data-testid={testid || 'runtime-estimate'}>
      <Info size={11} className="text-slate-400" />
      Estimated runtime: {estimate}
    </span>
  );
}
